import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Flex } from "antd";
import { createStyles } from "antd-style";

import { useJobRouteConfig } from "@av/apps/recruitment/ats-common/routes/jobs/jobs.route-config";
import { QUERY_PARAMS } from "@av/apps/recruitment/ats-common/routes/jobs/query-params";
import { Button } from "@av/components/atoms/Button";
import { Card } from "@av/components/atoms/Card";
import { Image } from "@av/components/atoms/Image";
import { Typography } from "@av/components/atoms/typography/Typography";
import { isSupportedLocale } from "@av/components/organisms/editor/localized-text-editor/text-editor-supported-locales";
import { FilterSearchParamMapper } from "../hooks/search-params-mapper";

const { Paragraph, Title } = Typography;

const useStyles = createStyles(({ css, token }) => ({
  card: css`
    height: inherit;
    box-shadow: ${token.boxShadowTertiary};

    @media (max-width: ${token.screenMD}px) {
      padding: ${token.paddingLG}px;
    }
  `,
  container: css`
    background: ${token.colorBgContainer};
  `,
  image: css`
    max-width: 250px;
    width: 100%;
    margin-bottom: ${token.marginLG}px;
  `,
  title: css`
    margin-top: 0;
    margin-bottom: ${token.marginXS}px;
    max-width: 640px;
  `,
  message: css`
    max-width: 640px;
    margin-bottom: ${token.margin}px;
  `,
}));

export function JobNotAvailable({ jobPostId }: { jobPostId: string }) {
  const { styles } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const jobsRoutes = useJobRouteConfig(companyId);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const returnSearchParams = new URLSearchParams(
    location.pathname.endsWith("/apply")
      ? ""
      : new FilterSearchParamMapper(searchParams).listSearchParams
  );
  const locale = searchParams.get(QUERY_PARAMS.locale);

  if (locale && isSupportedLocale(locale)) {
    returnSearchParams.set(QUERY_PARAMS.locale, locale);
  }

  returnSearchParams.sort();
  const returnSearch = returnSearchParams.toString();

  return (
    <Card
      testIdPrefix={`job-not-available-${jobPostId}`}
      className={styles.card}
      styles={{ body: { padding: 0, height: "100%" } }}
    >
      <Flex
        vertical
        align="center"
        justify="center"
        className={styles.container}
        style={{
          height: "100%",
          width: "100%",
          textAlign: "center",
          padding: "24px 16px",
        }}
      >
        <Image
          testIdPrefix="job-not-available"
          src="/errors/error.png"
          alt={t("recruitment.jobs.jobNotAvailable_alt")}
          preview={false}
          className={styles.image}
        />
        <Title level={3} testIdPrefix="job-not-available-title" className={styles.title}>
          {t("recruitment.jobs.jobNotAvailable_title")}
        </Title>
        <Paragraph
          testIdPrefix="job-not-available-message"
          type="secondary"
          className={styles.message}
        >
          {t("recruitment.jobs.jobNotAvailable_message")}
        </Paragraph>
        <Button
          type="link"
          onClick={() =>
            navigate({
              pathname: jobsRoutes.home.path(),
              search: returnSearch,
            })
          }
          testIdPrefix="job-not-available-refresh-jobs"
        >
          {t("recruitment.jobs.jobNotAvailable_refreshJobs")}
        </Button>
      </Flex>
    </Card>
  );
}
